import { Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";

export const RTFOSeismicityPanel = ({ darkMode, currentRTFO }) => {
  const theme = useTheme();

  const seismicSlots = [
    'padStartDate',
    'statsDate',
    'totalDaysPumping',
    'dailyPumpingHours',
    'totalEvents',
    'events1To2',
    'events2Plus',
    'dailyFrequencyAllEvents',
    'dailyFrequency2PlusEvents',
    'dailyCleanVolume'
  ]

  // const currentStageDate = new Date(currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.stageStartTimestamp)?.toISOString()?.split('T')?.[0];
  const currentStageDate = new Date(currentRTFO?.stageData?.[currentRTFO?.currentStageName]?.stageStartTimestamp)?.toLocaleDateString();

  return (
    <Paper elevation={0} style={{
      margin: '0px 20px',
      padding: '0px 20px',
      height: 80,
      overflowX: 'auto',
      display: 'flex', 
      flexDirection: 'row', 
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    }}>
      {seismicSlots?.map((metricName,i) => {
        const metricParam = getSeismicParamName(metricName);
        const metricUnit = getSeismicParamUnits(metricParam);
        const metricValue = getSeismicParamValue(metricParam, currentRTFO, currentStageDate);
        return (
          <div style={{display: 'flex', flexDirection: 'column', textOverflow: 'ellipsis', overflow: 'hidden'}} key={metricName+"-"+i}>
            <Typography variant="caption">{getSeismicName(metricName)}</Typography>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-end'}}>  
              <Typography variant="h6">{metricValue}</Typography>
              <Typography variant="caption" style={{marginLeft: 5, marginBottom: 4}} noWrap>{!isNaN(metricValue) ? metricUnit : ''}</Typography>
            </div>
          </div>
        );
      })}
    </Paper>
  );
};

export function getSeismicName(metric) {
  switch(metric) {
    case 'padStartDate':
      return 'Start Date';
    case 'statsDate':
      return 'Stats Date';
    case 'totalDaysPumping':
      return 'Days Pumping';
    case 'dailyPumpingHours':
      return 'Daily Pumping Hours';
    case 'totalEvents':
      return 'Total Events (All)';
    case 'totalDailyEvents':
      return 'Total Daily Events (All)';
    case 'events1To2':
      return 'Total Events (1-2 ML)';
    case 'dailyEvents1To2':
      return 'Total Daily Events (1-2 ML)';
    case 'events2Plus':
      return 'Total Events (2+ ML)';
    case 'dailyEvents2Plus':
      return 'Total Daily Events (2+ ML)';
    case 'dailyFrequencyAllEvents':
      return 'Daily Freq. (All)';
    case 'dailyFrequency2PlusEvents':
      return 'Daily Freq. (2+ ML)';
    case 'dailyCleanVolume':
      return 'Daily Clean Volume';
    case 'dailyTotalMeters':
      return 'Daily Total Meters';
    case 'dailyCleanVolumePerMeters':
      return 'Daily Clean Vol. per Meters';
    default:
      return metric;
  };
};

export function getSeismicParamName(param) {
  switch(param) {
    case 'Start Date':
      return 'padStartDate';
    case 'Stats Date':
      return 'statsDate';
    case 'Days Pumping':
      return 'totalDaysPumping';
    case 'Daily Pumping Hours':
      return 'dailyPumpingHours';
    case 'Total Events (All)':
      return 'totalEvents';
    case 'Total Daily Events (All)':
      return 'totalDailyEvents';
    case 'Total Events (1-2 ML)':
      return 'events1To2';
    case 'Total Daily Events (1-2 ML)':
      return 'dailyEvents1To2';
    case 'Total Events (2+ ML)':
      return 'events2Plus';
    case 'Total Daily Events (2+ ML)':
      return 'dailyEvents2Plus';
    case 'Daily Freq. (All)':
      return 'dailyFrequencyAllEvents';
    case 'Daily Freq. (2+ ML)':
      return 'dailyFrequency2PlusEvents';
    case 'Daily Clean Volume':
      return 'dailyCleanVolume';
    case 'Daily Total Meters':
      return 'dailyTotalMeters';
    case 'Daily Clean Vol. per Meters':
      return 'dailyCleanVolumePerMeters';
    default:
      return param;
  };
};

export function getSeismicParamUnits(metric) {
  switch(metric) {
    case 'totalDaysPumping':
      return 'days';
    case 'dailyCleanVolume':
      return 'm3';
    case 'dailyPumpingHours':
      return 'hrs';
    case 'dailyFrequencyAllEvents':
    case 'dailyFrequency2PlusEvents':
      return 'events/day';
    case 'dailyTotalMeters':
      return 'm/day';
    case 'dailyCleanVolumePerMeters':
      return 'm3/m/day';
    default:
      return '';
  };
};

export function getSeismicParamValue(metric, currentRTFO, date) {
  try {
    const stageData = currentRTFO?.stageData;
    const firstStageName = currentRTFO?.stages?.[0];
    const padStartTimestamp = currentRTFO?.stageData?.[firstStageName]?.stageStartTimestamp;
    const seismicData = currentRTFO?.seismicData?.['realtime'] || [];
    const statsStageNames = currentRTFO?.stages?.filter(s => new Date(stageData[s]?.stageStartTimestamp)?.toLocaleDateString() === date); 

    let statsTimestamp = new Date(date);
    statsTimestamp = statsTimestamp.setHours(23, 59, 59, 999);
    statsTimestamp = new Date(statsTimestamp)?.getTime();

    switch(metric) {
      case 'padStartDate':
        const padStartDate = new Date(padStartTimestamp)?.toLocaleDateString();
        return padStartDate;
      case 'statsDate':
        const statsDate = new Date(date)?.toLocaleDateString();
        return statsDate;
      case 'totalDaysPumping':
        const lastStageName = currentRTFO?.stages?.[currentRTFO?.stages?.length - 1];
        const lastStageData = currentRTFO?.stageData?.[lastStageName];
        const stageEndTime = lastStageData?.stageEndTimestamp;
        const totalDaysPumping = Math.ceil((stageEndTime - padStartTimestamp) / (24 * 60 * 60 * 1000));
        return +(totalDaysPumping?.toFixed(2)) || 0;
      case 'dailyPumpingHours':
        let dailyPumpingHours = 0;

        for (const stageName of statsStageNames) {
          dailyPumpingHours += (stageData[stageName]?.metrics?.stageTime / 60) || 0;
        };
        return +(Math.min(dailyPumpingHours, 24)?.toFixed(2)) || 0;
      case 'totalEvents':
        const totalEvents = seismicData?.filter(e => e.time <= statsTimestamp)?.length || 0;
        return totalEvents;
      case 'totalDailyEvents':
        const totalDailyEvents = seismicData?.filter(e => new Date(e.time)?.toLocaleDateString() === date)?.length || 0;
        return totalDailyEvents;
      case 'events1To2':
        const events1To2 = seismicData?.filter(e => e.time <= statsTimestamp && e.localMagnitude >= 1 && e.localMagnitude < 2)?.length || 0;
        return events1To2;
      case 'events2Plus':
        const events2Plus = seismicData?.filter(e => e.localMagnitude >= 2)?.length || 0;
        return events2Plus;
      case 'dailyEvents1To2':
        const dailyEvents1To2 = seismicData?.filter(e => new Date(e.time)?.toLocaleDateString() === date && e.localMagnitude >= 1 && e.localMagnitude < 2)?.length || 0;
        return dailyEvents1To2;
      case 'dailyEvents2Plus':
        const dailyEvents2Plus = seismicData?.filter(e => new Date(e.time)?.toLocaleDateString() === date && e.localMagnitude >= 2)?.length || 0;
        return dailyEvents2Plus;
      case 'dailyFrequencyAllEvents':
        const dailyFrequencyAllEvents = +((seismicData?.filter(e => new Date(e.time)?.toLocaleDateString() === date)?.length / 24)?.toFixed(2)) || 0;
        return dailyFrequencyAllEvents;
      case 'dailyFrequency2PlusEvents':
        const dailyFrequency2PlusEvents = +((seismicData?.filter(e => new Date(e.time)?.toLocaleDateString() === date && e.localMagnitude >= 2)?.length / 24)?.toFixed(2)) || 0;
        return dailyFrequency2PlusEvents;
      case 'dailyCleanVolume':
        let dailyCleanVolume = 0;

        for (const stageName of statsStageNames) {
          dailyCleanVolume += stageData[stageName]?.metrics?.totalCleanVolume;
        };

        return +(dailyCleanVolume?.toFixed(1)) || 0;
      case 'dailyTotalMeters':
        let dailyTotalMeters = 0;

        for (const stageName of statsStageNames) {
          dailyTotalMeters += stageData[stageName]?.metrics?.stageLength;
        };

        return +(dailyTotalMeters?.toFixed(1)) || 0;
      case 'dailyCleanVolumePerMeters':
        let totalCleanVolume = 0;
        let totalMeters = 0;

        for (const stageName of statsStageNames) {
          totalCleanVolume += stageData[stageName]?.metrics?.totalCleanVolume;
          totalMeters += stageData[stageName]?.metrics?.stageLength;
        };  

        const dailyCleanVolumePerMeters = totalCleanVolume / totalMeters;

        return +(dailyCleanVolumePerMeters?.toFixed(1)) || 0;
      default:
        return '';
    };
  } catch (error) {
    console.log(error);
    return 0;
  };
};
